import { type UseResizeDetectorReturn, useResizeDetector } from 'react-resize-detector';

import { type UseInlineSvgPreviewResult, useInlineSvgPreview } from '~/hooks/useInlineSvgPreview';
import { shouldContainTransparentPreview } from '~/utils/getTransparentPreviewSource';
import { isPreviewSrc } from '~/utils/isPreviewSrc';

export type UseTransparentThumbnailPreviewParams = {
  /** Paint a checkerboard behind alpha and keep the Imgix output from flattening to JPEG */
  preserveTransparency: boolean;
  /** True while the original (not the flattened processed fallback) is displayed */
  showCheckerboard: boolean;
  src: string | undefined;
  ext: string | undefined;
  animatedSrc: string | undefined;
  /** Source image size. When omitted, the transparent preview stays contained. */
  imageWidth: number | undefined;
  imageHeight: number | undefined;
};

export type UseTransparentThumbnailPreviewResult = {
  /** Attach only for transparent previews, so photo cards are never observed. */
  thumbnailBoxRef: UseResizeDetectorReturn<HTMLDivElement>['ref'];
  containedTransparency: boolean;
  inlineSvgPreviewUrl: UseInlineSvgPreviewResult['inlineSvgPreviewUrl'];
  markInlineSvgPreviewFailed: UseInlineSvgPreviewResult['markInlineSvgPreviewFailed'];
  /** True until the box is measured and the SVG preview settles; render nothing meanwhile. */
  isAwaitingTransparentPreview: boolean;
};

/**
 * Measures the thumbnail box for a transparent preview and picks contain vs cover, and swaps SVG
 * originals for a normalized data URL.
 */
export const useTransparentThumbnailPreview = ({
  preserveTransparency,
  showCheckerboard,
  src,
  ext,
  animatedSrc,
  imageWidth,
  imageHeight,
}: UseTransparentThumbnailPreviewParams): UseTransparentThumbnailPreviewResult => {
  const {
    ref: thumbnailBoxRef,
    width: thumbnailBoxWidth = 0,
    height: thumbnailBoxHeight = 0,
  } = useResizeDetector<HTMLDivElement>();
  // Contain only when cover would crop a logo-like share of the image (also after falling back to
  // the flattened processed JPEG); otherwise follow the thumbnail preference like any image.
  const containedTransparency =
    preserveTransparency &&
    shouldContainTransparentPreview({
      cardWidth: thumbnailBoxWidth,
      cardHeight: thumbnailBoxHeight,
      imageWidth,
      imageHeight,
    });
  // Imgix returns SVG originals raw (no fonts, no intrinsic size); the normalized data URL replaces it once ready.
  const { inlineSvgPreviewUrl, markInlineSvgPreviewFailed, isInlineSvgPreviewPending } = useInlineSvgPreview({
    src,
    ext,
    enabled: showCheckerboard && !animatedSrc && !!src && !isPreviewSrc(src),
  });
  // Nothing is requested until the box is measured (so the contain/cover choice, and with it the Imgix
  // `fit`, never flips after a request) and the SVG preview settles (so the raw SVG never paints first).
  const isAwaitingTransparentPreview =
    preserveTransparency &&
    (!thumbnailBoxWidth || !thumbnailBoxHeight || (showCheckerboard && isInlineSvgPreviewPending));

  return {
    thumbnailBoxRef,
    containedTransparency,
    inlineSvgPreviewUrl,
    markInlineSvgPreviewFailed,
    isAwaitingTransparentPreview,
  };
};
